import React from 'react';
import { styled } from '@benzinga/themetron';
import { css } from 'styled-components';
import * as Icons from '@benzinga/icons';

const InactiveCSS = css<{ hoverable?: boolean }>`
  fill: ${props => props.theme.colors.foregroundInactive};
  ${props =>
    props.hoverable &&
    css`
      &:hover {
        fill: ${props.theme.colors.foregroundActive};
      }
    `}
`;

const ActiveCSS = css`
  fill: ${props => props.theme.colors.foregroundActive};
`;

const PositiveCSS = css`
  fill: ${props => props.theme.colors.statistic.positive};
`;

const NegativeCSS = css`
  fill: ${props => props.theme.colors.statistic.negative};
`;

export interface SVGProps {
  className?: string;
  height?: string;
  onClick?: React.MouseEventHandler;
}

export type SVGPropType = React.SVGProps<SVGSVGElement> & {
  title?: string | undefined;
  size?: string;
};

const inactive = (icon: ReturnType<typeof styled>) => icon`${InactiveCSS}`;
const active = (icon: ReturnType<typeof styled>) => icon`${ActiveCSS}`;
const positive = (icon: ReturnType<typeof styled>) => icon`${PositiveCSS}`;
const negative = (icon: ReturnType<typeof styled>) => icon`${NegativeCSS}`;

const styledPositiveMap = new Map<React.FunctionComponent<SVGPropType>, React.FunctionComponent<SVGPropType>>();

export const styledPositive = <C extends React.FunctionComponent<SVGPropType>>(
  element: C,
): React.FunctionComponent<SVGPropType> => {
  const test = styledPositiveMap.get(element);
  if (test === undefined) {
    const val = positive(styled(element));
    styledPositiveMap.set(element, val as React.FunctionComponent<SVGPropType>);
    return val as React.FunctionComponent<SVGPropType>;
  }
  return test;
};

const styledActiveMap = new Map<React.FunctionComponent<SVGPropType>, React.FunctionComponent<SVGPropType>>();

export const styledActive = <C extends React.FunctionComponent<SVGPropType>>(
  element: C,
): React.FunctionComponent<SVGPropType> => {
  const test = styledActiveMap.get(element);
  if (test === undefined) {
    const val = active(styled(element));
    styledActiveMap.set(element, val as React.FunctionComponent<SVGPropType>);
    return val as React.FunctionComponent<SVGPropType>;
  }
  return test;
};

const styledInactiveMap = new Map<React.FunctionComponent<SVGPropType>, React.FunctionComponent<SVGPropType>>();

export const styledInactive = <C extends React.FunctionComponent<SVGPropType>>(
  element: C,
): React.FunctionComponent<SVGPropType> => {
  const test = styledInactiveMap.get(element);
  if (test === undefined) {
    const val = inactive(styled(element));
    styledInactiveMap.set(element, val as React.FunctionComponent<SVGPropType>);
    return val as React.FunctionComponent<SVGPropType>;
  }
  return test;
};

const styledNegativeMap = new Map<React.FunctionComponent<SVGPropType>, React.FunctionComponent<SVGPropType>>();

export const styledNegative = <C extends React.FunctionComponent<SVGPropType>>(
  element: C,
): React.FunctionComponent<SVGPropType> => {
  const test = styledNegativeMap.get(element);
  if (test === undefined) {
    const val = negative(styled(element));
    styledNegativeMap.set(element, val as React.FunctionComponent<SVGPropType>);
    return val as React.FunctionComponent<SVGPropType>;
  }
  return test;
};

export const Account = inactive(styled(Icons.Account));
export const Add = inactive(styled(Icons.Add));
export const AlertActive = inactive(styled(Icons.AlertActive));
export const AlertAdd = inactive(styled(Icons.AlertAdd));
export const Alert = inactive(styled(Icons.Alert));
export const AlertNone = inactive(styled(Icons.AlertNone));
export const AlertOff = inactive(styled(Icons.AlertOff));
export const AlertPaused = inactive(styled(Icons.AlertPaused));
export const AlertIcon = inactive(styled(Icons.AlertIcon));
export const AmericanExpress = inactive(styled(Icons.AmericanExpress));
export const BZ = inactive(styled(Icons.BZ));
export const BzEdgeLight = inactive(styled(Icons.BzEdgeLight));
export const BallotLight = inactive(styled(Icons.BallotLight));
export const BarsRegular = inactive(styled(Icons.BarsRegular));
export const Bear1x = negative(styled(Icons.Bear1x));
export const Bear2x = negative(styled(Icons.Bear2x));
export const Bear3x = negative(styled(Icons.Bear3x));
export const Billing = inactive(styled(Icons.Billing));
export const Bull1x = positive(styled(Icons.Bull1x));
export const Bull2x = positive(styled(Icons.Bull2x));
export const Bull3x = positive(styled(Icons.Bull3x));
export const Calendar = inactive(styled(Icons.Calendar));
export const CalendarIcon = inactive(styled(Icons.CalendarIcon));
export const Candlestick = inactive(styled(Icons.Candlestick));
export const Categories = inactive(styled(Icons.Categories));
export const Change = inactive(styled(Icons.Change));
export const Chat = inactive(styled(Icons.Chat));
export const ChatStream = inactive(styled(Icons.ChatSteam));
export const ChatIcon = inactive(styled(Icons.ChatIcon));
export const MagnifyGlass = inactive(styled(Icons.MagnifyGlass));
export const CheckboxActive = inactive(styled(Icons.CheckboxActive));
export const CheckmarkAlert = inactive(styled(Icons.CheckmarkAlert));
export const CheckboxInactive = inactive(styled(Icons.CheckboxInactive));
export const CheckboxIndeterminate = inactive(styled(Icons.CheckboxIndeterminate));
export const Checkmark = inactive(styled(Icons.Checkmark));
export const ChevronDown = inactive(styled(Icons.ChevronDown));
export const ChevronLeft = inactive(styled(Icons.ChevronLeft));
export const ChevronRight = inactive(styled(Icons.ChevronRight));
export const ChevronUp = inactive(styled(Icons.ChevronUp));
export const Chrome = inactive(styled(Icons.Chrome));
export const Close = inactive(styled(Icons.Close));
export const Code = inactive(styled(Icons.Code));
export const Copy = inactive(styled(Icons.Copy));
export const CreditCardOutline = inactive(styled(Icons.CreditCardOutline));
export const Debug = inactive(styled(Icons.Debug));
export const Delete = inactive(styled(Icons.Delete));
export const Details = inactive(styled(Icons.Details));
export const DinersClub = inactive(styled(Icons.DinersClub));
export const Discover = inactive(styled(Icons.Discover));
export const DownArrow = inactive(styled(Icons.DownArrow));
export const Download = inactive(styled(Icons.Download));
export const Edge = inactive(styled(Icons.Edge));
export const Edit = inactive(styled(Icons.Edit));
export const Email = inactive(styled(Icons.Email));
export const EmptyStar = inactive(styled(Icons.EmptyStar));
export const Error = inactive(styled(Icons.Error));
export const Expanded = inactive(styled(Icons.Expanded));
export const Explore = inactive(styled(Icons.Explore));
export const Facebook = inactive(styled(Icons.Facebook));
export const Feedback = inactive(styled(Icons.Feedback));

export const Filter = inactive(styled(Icons.Filter));
export const Firefox = inactive(styled(Icons.Firefox));
export const Flask = inactive(styled(Icons.Flask));
export const FullStar = inactive(styled(Icons.FullStar));
export const Globe = inactive(styled(Icons.Globe));
export const Goal = positive(styled(Icons.Goal));
export const GoalStriked = negative(styled(Icons.GoalStriked));
export const GripLinesRegular = inactive(styled(Icons.GripLinesRegular));
export const HalfStar = inactive(styled(Icons.HalfStar));
export const Headlines = inactive(styled(Icons.Headlines));
export const Help = inactive(styled(Icons.Help));
export const History = inactive(styled(Icons.History));
export const HorizontalRuleRegular = inactive(styled(Icons.HorizontalRuleRegular));
export const Ideas = inactive(styled(Icons.Ideas));
export const Info = inactive(styled(Icons.Info));
export const JCB = inactive(styled(Icons.JCB));
export const Home = inactive(styled(Icons.Home));
export const Layout = inactive(styled(Icons.Layout));
export const LeftArrow = inactive(styled(Icons.LeftArrow));
export const LeftMenu = inactive(styled(Icons.LeftMenu));
export const Line = inactive(styled(Icons.Line));
export const Link = inactive(styled(Icons.Link));
export const LinkUp = inactive(styled(Icons.LinkUp));
export const LinkDown = inactive(styled(Icons.LinkDown));
export const Linkedin = inactive(styled(Icons.Linkedin));
export const Load = inactive(styled(Icons.Load));
export const Lock = inactive(styled(Icons.Lock));
export const Logout = inactive(styled(Icons.Logout));
export const MasterCard = inactive(styled(Icons.MasterCard));
export const Menu = inactive(styled(Icons.Menu));
export const Message = inactive(styled(Icons.Message));
export const More = inactive(styled(Icons.More));
export const Movers = inactive(styled(Icons.Movers));
export const MoversIcon = inactive(styled(Icons.MoversIcon));
export const Newsfeed = inactive(styled(Icons.Newsfeed));
export const NewsfeedIcon = inactive(styled(Icons.NewsfeedIcon));
export const OptionsIcon = inactive(styled(Icons.OptionsIcon));
export const PositionWidgetLeftIcon = inactive(styled(Icons.PositionWidgetLeft));
export const Phone = inactive(styled(Icons.Phone));
export const Play = inactive(styled(Icons.Play));
export const Popout = inactive(styled(Icons.Popout));
export const PressRelease = inactive(styled(Icons.PressRelease));
export const Profile = inactive(styled(Icons.Profile));
export const Refresh = inactive(styled(Icons.Refresh));
export const Reorder = inactive(styled(Icons.Reorder));
export const RightArrow = inactive(styled(Icons.RightArrow));
export const RightMenu = inactive(styled(Icons.RightMenu));
export const Scanner = inactive(styled(Icons.Scanner));
export const ScreenerIcon = inactive(styled(Icons.ScreenerIcon));
export const ScannerIcon = inactive(styled(Icons.ScannerIcon));
export const Sectors = inactive(styled(Icons.Sectors));
export const Settings = inactive(styled(Icons.Settings));
export const Signals = inactive(styled(Icons.Signals));
export const SignalsIcon = inactive(styled(Icons.SignalsIcon));
export const SmallSearch = inactive(styled(Icons.SmallSearch));
export const Squawk = inactive(styled(Icons.Squawk));
export const SquawkIcon = inactive(styled(Icons.SquawkIcon));
export const Stocktwits = inactive(styled(Icons.Stocktwits));
export const Subdirectory = inactive(styled(Icons.Subdirectory));
export const Supervisor = inactive(styled(Icons.Supervisor));
export const Sync = inactive(styled(Icons.Sync));
export const TextHeightLarge = inactive(styled(Icons.TextHeightLarge));
export const TextHeightSmall = inactive(styled(Icons.TextHeightSmall));
export const TextSizeLarge = inactive(styled(Icons.TextSizeLarge));
export const TextSizeSmall = inactive(styled(Icons.TextSizeSmall));
export const Trash = inactive(styled(Icons.Trash));
export const TrendingDown = inactive(styled(Icons.TrendingDown));
export const TrendingUp = inactive(styled(Icons.TrendingUp));
export const Twitter = inactive(styled(Icons.Twitter));
export const UpArrow = inactive(styled(Icons.UpArrow));
export const Up = inactive(styled(Icons.Up));
export const VerticalSplit = inactive(styled(Icons.VerticalSplit));
export const Visa = inactive(styled(Icons.Visa));
export const VolumeDown = inactive(styled(Icons.VolumeDown));
export const VolumeMute = inactive(styled(Icons.VolumeMute));
export const VolumeOff = inactive(styled(Icons.VolumeOff));
export const VolumeUp = inactive(styled(Icons.VolumeUp));
export const Warning = inactive(styled(Icons.Warning));
export const Watchlist = inactive(styled(Icons.Watchlist));
export const Wrap = inactive(styled(Icons.Wrap));
export const Location = inactive(styled(Icons.Location));
export const PieChart = inactive(styled(Icons.Fontawesome.PieChart));
export const TradingPhone = active(styled(Icons.TradingPhone));
export const TechEnvelope = active(styled(Icons.TechEnvelope));

export const ActiveAccount = active(styled(Icons.Account));
export const ActiveAdd = active(styled(Icons.Add));
export const ActiveAlertActive = active(styled(Icons.AlertActive));
export const ActiveAlertAdd = active(styled(Icons.AlertAdd));
export const ActiveAlert = active(styled(Icons.Alert));
export const ActiveAlertNone = active(styled(Icons.AlertNone));
export const ActiveAlertOff = active(styled(Icons.AlertOff));
export const ActiveAlertPaused = active(styled(Icons.AlertPaused));
export const ActiveAmericanExpress = active(styled(Icons.AmericanExpress));
export const ActiveBZ = active(styled(Icons.BZ));
export const ActiveBallotLight = active(styled(Icons.BallotLight));
export const ActiveBarsRegular = active(styled(Icons.BarsRegular));
export const ActiveBear1x = active(styled(Icons.Bear1x));
export const ActiveBear2x = active(styled(Icons.Bear2x));
export const ActiveBear3x = active(styled(Icons.Bear3x));
export const ActiveBilling = active(styled(Icons.Billing));
export const ActiveBull1x = active(styled(Icons.Bull1x));
export const ActiveBull2x = active(styled(Icons.Bull2x));
export const ActiveBull3x = active(styled(Icons.Bull3x));
export const ActiveCalendar = active(styled(Icons.Calendar));
export const ActiveCandlestick = active(styled(Icons.Candlestick));
export const ActiveCategories = active(styled(Icons.Categories));
export const ActiveChange = active(styled(Icons.Change));
export const ActiveChat = active(styled(Icons.Chat));
export const ActiveChatStream = active(styled(Icons.ChatSteam));
export const ActiveCheckboxActive = active(styled(Icons.CheckboxActive));
export const ActiveCheckboxInactive = active(styled(Icons.CheckboxInactive));
export const ActiveCheckboxIndeterminate = active(styled(Icons.CheckboxIndeterminate));
export const ActiveCheckmark = active(styled(Icons.Checkmark));
export const ActiveChevronDown = active(styled(Icons.ChevronDown));
export const ActiveChevronLeft = active(styled(Icons.ChevronLeft));
export const ActiveChevronRight = active(styled(Icons.ChevronRight));
export const ActiveChevronUp = active(styled(Icons.ChevronUp));
export const ActiveChrome = active(styled(Icons.Chrome));
export const ActiveClose = active(styled(Icons.Close));
export const ActiveCode = active(styled(Icons.Code));
export const ActiveCopy = active(styled(Icons.Copy));
export const ActiveCreditCardOutline = active(styled(Icons.CreditCardOutline));
export const ActiveDebug = active(styled(Icons.Debug));
export const ActiveDelete = active(styled(Icons.Delete));
export const ActiveDetails = active(styled(Icons.Details));
export const ActiveDinersClub = active(styled(Icons.DinersClub));
export const ActiveDiscover = active(styled(Icons.Discover));
export const ActiveDownArrow = active(styled(Icons.DownArrow));
export const ActiveDownload = active(styled(Icons.Download));
export const ActiveEdge = active(styled(Icons.Edge));
export const ActiveEdit = active(styled(Icons.Edit));
export const ActiveEmail = active(styled(Icons.Email));
export const ActiveEmptyStar = active(styled(Icons.EmptyStar));
export const ActiveError = active(styled(Icons.Error));
export const ActiveExpanded = active(styled(Icons.Expanded));
export const ActiveExplore = active(styled(Icons.Explore));
export const ActiveFacebook = active(styled(Icons.Facebook));
export const ActiveFeedback = active(styled(Icons.Feedback));
export const ActiveFilter = active(styled(Icons.Filter));
export const ActiveFirefox = active(styled(Icons.Firefox));
export const ActiveFlask = active(styled(Icons.Flask));
export const ActiveFullStar = active(styled(Icons.FullStar));
export const ActiveGlobe = active(styled(Icons.Globe));
export const ActiveGripLinesRegular = active(styled(Icons.GripLinesRegular));
export const ActiveHalfStar = active(styled(Icons.HalfStar));
export const ActiveHeadlines = active(styled(Icons.Headlines));
export const ActiveHelp = active(styled(Icons.Help));
export const ActiveHistory = active(styled(Icons.History));
export const ActiveHorizontalRuleRegular = active(styled(Icons.HorizontalRuleRegular));
export const ActiveInfo = active(styled(Icons.Info));
export const ActiveJCB = active(styled(Icons.JCB));
export const ActiveLayout = active(styled(Icons.Layout));
export const ActiveLeftArrow = active(styled(Icons.LeftArrow));
export const ActiveLeftMenu = active(styled(Icons.LeftMenu));
export const ActiveLine = active(styled(Icons.Line));
export const ActiveLink = active(styled(Icons.Link));
export const ActiveLinkUp = active(styled(Icons.LinkUp));
export const ActiveLinkDown = active(styled(Icons.LinkDown));
export const ActiveLinkedin = active(styled(Icons.Linkedin));
export const ActiveLoad = active(styled(Icons.Load));
export const ActiveLock = active(styled(Icons.Lock));
export const ActiveLogout = active(styled(Icons.Logout));
export const ActiveMasterCard = active(styled(Icons.MasterCard));
export const ActiveMenu = active(styled(Icons.Menu));
export const ActiveMessage = active(styled(Icons.Message));
export const ActiveMore = active(styled(Icons.More));
export const ActiveMovers = active(styled(Icons.Movers));
export const ActiveNewsfeed = active(styled(Icons.Newsfeed));
export const ActivePhone = active(styled(Icons.Phone));
export const ActivePlay = active(styled(Icons.Play));
export const ActivePopout = active(styled(Icons.Popout));
export const ActivePositionWidgetLeftIcon = active(styled(Icons.PositionWidgetLeft));
export const ActivePressRelease = active(styled(Icons.PressRelease));
export const ActiveRefresh = active(styled(Icons.Refresh));
export const ActiveReorder = active(styled(Icons.Reorder));
export const ActiveRightArrow = active(styled(Icons.RightArrow));
export const ActiveRightMenu = active(styled(Icons.RightMenu));
export const ActiveScreener = active(styled(Icons.Scanner));
export const ActiveSectors = active(styled(Icons.Sectors));
export const ActiveSettings = active(styled(Icons.Settings));
export const ActiveSignals = active(styled(Icons.Signals));
export const ActiveSmallSearch = active(styled(Icons.SmallSearch));
export const ActiveSquawk = active(styled(Icons.Squawk));
export const ActiveStocktwits = active(styled(Icons.Stocktwits));
export const ActiveSubdirectory = active(styled(Icons.Subdirectory));
export const ActiveSupervisor = active(styled(Icons.Supervisor));
export const ActiveSync = active(styled(Icons.Sync));
export const ActiveTextHeightLarge = active(styled(Icons.TextHeightLarge));
export const ActiveTextHeightSmall = active(styled(Icons.TextHeightSmall));
export const ActiveTextSizeLarge = active(styled(Icons.TextSizeLarge));
export const ActiveTextSizeSmall = active(styled(Icons.TextSizeSmall));
export const ActiveTrash = active(styled(Icons.Trash));
export const ActiveTrendingDown = active(styled(Icons.TrendingDown));
export const ActiveTrendingUp = active(styled(Icons.TrendingUp));
export const ActiveTwitter = active(styled(Icons.Twitter));
export const ActiveUpArrow = active(styled(Icons.UpArrow));
export const ActiveUp = active(styled(Icons.Up));
export const AlarmSmall = active(styled(Icons.AlarmSmall));
export const ActiveVerticalSplit = active(styled(Icons.VerticalSplit));
export const ActiveVisa = active(styled(Icons.Visa));
export const ActiveVolumeDown = active(styled(Icons.VolumeDown));
export const ActiveVolumeMute = active(styled(Icons.VolumeMute));
export const ActiveVolumeOff = active(styled(Icons.VolumeOff));
export const ActiveVolumeUp = active(styled(Icons.VolumeUp));
export const ActiveWarning = active(styled(Icons.Warning));
export const ActiveWatchlist = active(styled(Icons.Watchlist));
export const ActiveWrap = active(styled(Icons.Wrap));
export const Trial = active(styled(Icons.Trial));

export const ActivePieChart = active(styled(Icons.Fontawesome.PieChart));
